import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteHook } from '@shared/hooks';
import { AmountUtils, FormatUtils, RoutePaths } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import { Table, TableCellType } from '@shared/widgets';

interface Props {
  isLoading?: boolean;
  accountType: AccountType;
  accounts: AccountResponse[];
}

export const AccountsTable = (props: Props) => {
  const { navigate } = useRouteHook();

  const { t } = useTranslation('accounts');

  const cardColumns = useMemo(
    () => [
      t('card.cardColumns.account'),
      t('card.cardColumns.currency'),
      t('card.cardColumns.product'),
      t('card.cardColumns.status'),
      t('card.cardColumns.limit'),
      t('card.cardColumns.balance'),
    ],
    [t],
  );
  const accountColumns = useMemo(
    () => [
      t('payment.accountColumns.accountName'),
      t('payment.accountColumns.product'),
      t('payment.accountColumns.account'),
      t('payment.accountColumns.currency'),
      t('payment.accountColumns.iban'),
      t('payment.accountColumns.status'),
      t('payment.accountColumns.balance'),
    ],
    [t],
  );
  const data = useMemo(() => props.accounts.filter((x) => x.type == props.accountType), [props.accounts, props.accountType]);

  if (props.accountType === AccountType.Card)
    return (
      <Table
        isLoading={props.isLoading}
        isScrollable
        columns={cardColumns}
        items={data.map((x) => [
          { type: TableCellType.Text, value: x.accountNumber },
          { type: TableCellType.Text, value: x.currency },
          { type: TableCellType.Text, value: x.productName },
          { type: TableCellType.Text, value: t(`statuses.account.${x.status}`, { ns: 'common' }).toUpperCase() },
          { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(x.creditLimit ?? 0, x.currency) },
          { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(x.balance ?? 0, x.currency) },
        ])}
        onClick={(rowIndex) => {
          const accountId = data.find((_, index) => rowIndex === index)?.id;
          navigate(RoutePaths.account.onNavigate(accountId!));
        }}
      />
    );
  return (
    <Table
      isLoading={props.isLoading}
      isScrollable
      columns={accountColumns}
      items={data.map((x) => [
        { type: TableCellType.Text, value: x.name || FormatUtils.formatAccountNumber(x.accountNumber) },
        { type: TableCellType.Text, value: x.productName },
        { type: TableCellType.Text, value: x.accountNumber },
        { type: TableCellType.Text, value: x.currency },
        { type: TableCellType.Text, value: x.iban },
        { type: TableCellType.Text, value: t(`statuses.account.${x.status}`, { ns: 'common' }).toUpperCase() },
        { type: TableCellType.Text, value: AmountUtils.transformToAmountWithCurrency(x.availableBalance ?? 0, x.currency) },
      ])}
      onClick={(rowIndex) => {
        const accountId = data.find((_, index) => rowIndex === index)?.id;
        navigate(RoutePaths.account.onNavigate(accountId!));
      }}
    />
  );
};
